<template>
    <main class="flex-1 relative overflow-y-auto py-6 focus:outline-none" tabindex="0">
        
            <div class="max-w-7xl mx-auto sm:px-6 lg:px-8 px-5">
                <div class="md:flex md:items-center md:justify-between">
                    <div class="flex-1 min-w-0">
                        <h1 class="py-0.5 text-2xl font-semibold text-gray-900">{{ $t('Edit customer') }}</h1>
                    </div>
                    <div class="mt-4 flex md:mt-0 md:ml-4">
                        <button
                            class="btn btn-red shadow-sm rounded-md"
                            type="button"
                            @click="deleteReportMediumModal = true"
                        >
                            {{ $t('Delete customer') }}
                        </button>
                    </div>
                </div>
            </div>
            <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div class="mt-6 shadow sm:rounded-lg">
                    <loading :status="loading.form"/>
                    <div class="bg-white md:grid md:grid-cols-3 md:gap-6 px-4 py-5 sm:p-6">
                        <div class="md:col-span-1">
                            <h3 class="text-lg font-medium leading-6 text-gray-900">{{ $t('Customer details') }}</h3>
                            <p class="mt-1 text-sm leading-5 text-gray-500">
                                {{ $t('Customer details and classification') }}.
                            </p>
                        </div>
                        <div class="mt-5 md:mt-0 md:col-span-2">
                            <form id="customerForm" @submit.prevent="saveCustomer">
                                <div class="grid grid-cols-3 gap-6">
                                    <div class="col-span-3">
                                        <label class="block text-sm font-medium leading-5 text-gray-700" for="customerName">{{ $t('Name') }}</label>
                                        <div class="mt-1 relative rounded-md shadow-sm">
                                            <input
                                                id="customerName"
                                                v-model="customer.name"
                                                :placeholder="$t('Name')"
                                                class="disabled:bg-gray-200 disabled:cursor-not-allowed form-input block w-full transition duration-150 ease-in-out sm:text-sm sm:leading-5"
                                                v-uppercase
                                            >
                                        </div>
                                        <div v-if="errors.name" class="col-span-3">
                                            <p class="text-red-600 text-sm mt-2">{{ errors.name }}</p>
                                        </div>
                                    </div>
                                    <div class="col-span-3">
                                        <label class="block text-sm font-medium leading-5 text-gray-700" for="customerPhone">{{ $t('Phone') }}</label>
                                        <div class="mt-1 relative rounded-md shadow-sm">
                                            <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                                <span class="text-gray-500 sm:text-sm">+52</span>
                                            </div>
                                            <input
                                                id="customerPhone"
                                                v-model="customer.phone"
                                                :placeholder="$t('Phone')"
                                                class="disabled:bg-gray-200 disabled:cursor-not-allowed form-input block w-full pl-12 transition duration-150 ease-in-out sm:text-sm sm:leading-5"
                                                maxlength="10"
                                                v-uppercase
                                            >
                                        </div>
                                        <div v-if="errors.phone" class="col-span-3">
                                            <p class="text-red-600 text-sm mt-2">{{ errors.phone }}</p>
                                        </div>
                                    </div>
                                    <div class="col-span-3">
                                        <label class="block text-sm font-medium leading-5 text-gray-700" for="customerNeighborhood">{{ $t('Ticket neighborhood') }}</label>
                                        <div class="mt-1 relative rounded-md shadow-sm">
                                            <input-select
                                                id="customerNeighborhood"
                                                v-model="customer.neighborhood_id"
                                                :options="neighborhoodList"
                                                option-label="name"
                                                searchable
                                            >
                                                <template v-slot:selectedOption="props">
                                                    <template v-if="props && !props.anySelected">
                                                        <span class="block truncate">{{ $t('Select an option') }}</span>
                                                    </template>
                                                    <template v-else>
                                                        <span class="block truncate">{{ props.option.name }}</span>
                                                    </template>
                                                </template>
    
                                                <template v-slot:selectOption="props">
                                                    <div :class="props.option.id === customer.neighborhood_id ? 'font-semibold' : 'font-normal'" class="font-normal block truncate">
                                                        {{ props.option.name }}
                                                    </div>
                                                </template>
                                            </input-select>
                                        </div>
                                        <div v-if="errors.neighborhood_id" class="col-span-3">
                                            <p class="text-red-600 text-sm mt-2">{{ errors.neighborhood_id }}</p>
                                        </div>
                                    </div>
                                    <div class="col-span-3">
                                        <label class="block text-sm font-medium leading-5 text-gray-700" for="street">{{ $t('Ticket street') }}</label>
                                        <div class="mt-1 relative rounded-md shadow-sm">
                                            <input
                                                id="street"
                                                v-model="customer.street"
                                                :placeholder="$t('Ticket street')"
                                                class="disabled:bg-gray-200 disabled:cursor-not-allowed form-input block w-full transition duration-150 ease-in-out sm:text-sm sm:leading-5"
                                                v-uppercase
                                            >
                                        </div>
                                        <div v-if="errors.street" class="col-span-3">
                                            <p class="text-red-600 text-sm mt-2">{{ errors.street }}</p>
                                        </div>
                                    </div>
                                    <div class="col-span-3">
                                        <label class="block text-sm font-medium leading-5 text-gray-700" for="letter">{{ $t('Ticket letter') }}</label>
                                        <div class="mt-1 relative rounded-md shadow-sm">
                                            <input
                                                id="letter"
                                                v-model="customer.letter"
                                                :placeholder="$t('Ticket letter')"
                                                class="disabled:bg-gray-200 disabled:cursor-not-allowed form-input block w-full transition duration-150 ease-in-out sm:text-sm sm:leading-5"
                                                v-uppercase
                                            >
                                        </div>
                                        <div v-if="errors.letter" class="col-span-3">
                                            <p class="text-red-600 text-sm mt-2">{{ errors.letter }}</p>
                                        </div>
                                    </div>
                                    <div class="col-span-3">
                                        <label class="block text-sm font-medium leading-5 text-gray-700" for="number">{{ $t('Ticket number') }}</label>
                                        <div class="mt-1 relative rounded-md shadow-sm">
                                            <input
                                                id="number"
                                                v-model="customer.number"
                                                :placeholder="$t('Ticket number')"
                                                class="disabled:bg-gray-200 disabled:cursor-not-allowed form-input block w-full transition duration-150 ease-in-out sm:text-sm sm:leading-5"
                                                v-uppercase
                                            >
                                        </div>
                                        <div v-if="errors.number" class="col-span-3">
                                            <p class="text-red-600 text-sm mt-2">{{ errors.number }}</p>
                                        </div>
                                    </div>
                                    <div class="col-span-3">
                                        <label class="block text-sm font-medium leading-5 text-gray-700" for="letter">{{ $t('Ticket letter') }}</label>
                                        <div class="mt-1 relative rounded-md shadow-sm">
                                            <input
                                                id="letter"
                                                v-model="customer.letter2"
                                                :placeholder="$t('Ticket letter')"
                                                class="disabled:bg-gray-200 disabled:cursor-not-allowed form-input block w-full transition duration-150 ease-in-out sm:text-sm sm:leading-5"
                                                v-uppercase
                                            >
                                        </div>
                                        <div v-if="errors.letter2" class="col-span-3">
                                            <p class="text-red-600 text-sm mt-2">{{ errors.letter2 }}</p>
                                        </div>
                                    </div>
                                    <div class="col-span-3">
                                        <label class="block text-sm font-medium leading-5 text-gray-700" for="crossover">{{ $t('Crossover') }}</label>
                                        <div class="mt-1 relative rounded-md shadow-sm">
                                            <input
                                                type="text"
                                                id="crossover"
                                                v-model="customer.crossover"
                                                class="form-input block w-full transition duration-150 ease-in-out sm:text-sm sm:leading-5"
                                                v-uppercase
                                            />
                                        </div>
                                        <div v-if="errors.crossover" class="col-span-3">
                                            <p class="text-red-600 text-sm mt-2">{{ errors.crossover }}</p>
                                        </div>
                                    </div>
                                    <div class="col-span-3">
                                        <label class="block text-sm font-medium leading-5 text-gray-700" for="letter">{{ $t('Ticket letter') }}</label>
                                        <div class="mt-1 relative rounded-md shadow-sm">
                                            <input
                                                id="letter"
                                                v-model="customer.letter3"
                                                :placeholder="$t('Ticket letter')"
                                                class="disabled:bg-gray-200 disabled:cursor-not-allowed form-input block w-full transition duration-150 ease-in-out sm:text-sm sm:leading-5"
                                                v-uppercase
                                            >
                                        </div>
                                        <div v-if="errors.letter3" class="col-span-3">
                                            <p class="text-red-600 text-sm mt-2">{{ errors.letter3 }}</p>
                                        </div>
                                    </div>
                                    <div class="col-span-3">
                                        <label class="block text-sm font-medium leading-5 text-gray-700" for="crossover2">{{ $t('Crossover2') }}</label>
                                        <div class="mt-1 relative rounded-md shadow-sm">
                                            <input
                                                id="crossover2"
                                                v-model="customer.crossover2"
                                                :placeholder="$t('Crossover2')"
                                                class="form-input block w-full transition duration-150 ease-in-out sm:text-sm sm:leading-5"
                                                v-uppercase
                                            />
                                        </div>
                                        <div v-if="errors.crossover2" class="col-span-3">
                                            <p class="text-red-600 text-sm mt-2">{{ errors.crossover2 }}</p>
                                        </div>
                                    </div>
                                    <div class="col-span-3">
                                        <label class="block text-sm font-medium leading-5 text-gray-700" for="letter">{{ $t('Ticket letter') }}</label>
                                        <div class="mt-1 relative rounded-md shadow-sm">
                                            <input
                                                id="letter"
                                                v-model="customer.letter3"
                                                :placeholder="$t('Ticket letter')"
                                                class="disabled:bg-gray-200 disabled:cursor-not-allowed form-input block w-full transition duration-150 ease-in-out sm:text-sm sm:leading-5"
                                                v-uppercase
                                            >
                                        </div>
                                        <div v-if="errors.letter3" class="col-span-3">
                                            <p class="text-red-600 text-sm mt-2">{{ errors.letter3 }}</p>
                                        </div>
                                    </div>
                                    <div class="col-span-3">
                                        <label class="block text-sm font-medium leading-5 text-gray-700" for="address">{{ $t('Address') }}</label>
                                        <div class="mt-1 relative rounded-md shadow-sm">
                                            <input
                                                id="address"
                                                v-model="customer.address"
                                                :placeholder="$t('Address')"
                                                class="disabled:bg-gray-200 disabled:cursor-not-allowed form-input block w-full transition duration-150 ease-in-out sm:text-sm sm:leading-5"
                                                v-uppercase
                                            >
                                        </div>
                                        <div v-if="errors.address" class="col-span-3">
                                            <p class="text-red-600 text-sm mt-2">{{ errors.address }}</p>
                                        </div>
                                    </div>
                                    <div class="col-span-3">
                                        <label class="block text-sm font-medium leading-5 text-gray-700" for="mapUrl">{{ $t('Map url') }}</label>
                                        <div class="mt-1 relative rounded-md shadow-sm">
                                            <input
                                                id="mapUrl"
                                                v-model="customer.map_url"
                                                :placeholder="$t('Map Url')"
                                                class="disabled:bg-gray-200 disabled:cursor-not-allowed form-input block w-full transition duration-150 ease-in-out sm:text-sm sm:leading-5"
                                            >
                                        </div>
                                        <div v-if="errors.map_url" class="col-span-3">
                                            <p class="text-red-600 text-sm mt-2">{{ errors.map_url }}</p>
                                        </div>
                                    </div>
                                    <div class="col-span-3">
                                        <label class="block text-sm font-medium leading-5 text-gray-700" for="referency">{{ $t('Referency') }}</label>
                                        <div class="mt-1 relative rounded-md shadow-sm">
                                            <input
                                                id="referency"
                                                v-model="customer.referency"
                                                :placeholder="$t('Referency')"
                                                class="disabled:bg-gray-200 disabled:cursor-not-allowed form-input block w-full transition duration-150 ease-in-out sm:text-sm sm:leading-5"
                                                v-uppercase
                                            >
                                        </div>
                                        <div v-if="errors.referency" class="col-span-3">
                                            <p class="text-red-600 text-sm mt-2">{{ errors.referency }}</p>
                                        </div>
                                    </div>
                                </div>
                            </form>
                        </div>
                    </div>
                    <div class="bg-gray-100 text-right px-4 py-3 sm:px-6">
                        <div class="inline-flex">
                            <router-link
                                class="btn btn-secondary shadow-sm rounded-md mr-4"
                                to="/dashboard/admin/customers"
                            >
                                {{ $t('Cancel') }}
                            </router-link>
                            <button
                                form="customerForm"
                                class="btn btn-green shadow-sm rounded-md"
                                type="submit"
                            >
                                {{ $t('Save') }}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
            <div v-show="deleteReportMediumModal" class="fixed z-20 inset-0 overflow-y-auto">
            <div class="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
                <transition
                    duration="300"
                    enter-active-class="ease-out duration-300"
                    enter-class="opacity-0"
                    enter-to-class="opacity-100"
                    leave-active-class="ease-in duration-200"
                    leave-class="opacity-100"
                    leave-to-class="opacity-0"
                >
                    <div v-show="deleteReportMediumModal" class="fixed inset-0 transition-opacity">
                        <div class="absolute inset-0 bg-gray-500 opacity-75"></div>
                    </div>
                </transition>
                <span class="hidden sm:inline-block sm:align-middle sm:h-screen"></span>&#8203;
                <transition
                    enter-active-class="ease-out duration-300"
                    enter-class="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                    enter-to-class="opacity-100 translate-y-0 sm:scale-100"
                    leave-active-class="ease-in duration-200"
                    leave-class="opacity-100 translate-y-0 sm:scale-100"
                    leave-to-class="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                >
                    <div
                        v-show="deleteReportMediumModal"
                        aria-labelledby="modal-headline"
                        aria-modal="true"
                        class="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full"
                        role="dialog"
                    >
                        <div class="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                            <div class="sm:flex sm:items-start">
                                <div class="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                                    <svg-vue class="h-6 w-6 pb-1 text-red-600" icon="font-awesome.exclamation-triangle-light"></svg-vue>
                                </div>
                                <div class="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                                    <h3 id="modal-headline" class="text-lg leading-6 font-medium text-gray-900">
                                        {{ $t('Delete customer') }}
                                    </h3>
                                    <div class="mt-2">
                                        <p class="text-sm leading-5 text-gray-500">
                                            {{ $t('Are you sure you want to delete the customer?') }}
                                            {{ $t('All data will be permanently removed') }}.
                                            {{ $t('Tickets with this department will be deleted') }}.
                                            {{ $t('This action cannot be undone') }}.
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div class="bg-gray-100 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                            <button
                                class="btn btn-red mr-2 sm:mr-0"
                                type="button"
                                @click="deleteCustomer"
                            >
                                {{ $t('Delete customer') }}
                            </button>
                            <button
                                class="btn btn-white mr-0 sm:mr-2"
                                type="button"
                                @click="deleteReportMediumModal = false"
                            >
                                {{ $t('Cancel') }}
                            </button>
                        </div>
                    </div>
                </transition>
            </div>
        </div>
    </main>
</template>

<script>
import { TheMask } from 'vue-the-mask';
import * as yup from 'yup';

export default {
    name: "new",
    components: {
        TheMask
    },
    metaInfo() {
        return {
            title: this.$i18n.t('Create ticket')
        }
    },
    data() {
        return {
            loading: {
                form: true,
                file: false,
            },
            deleteReportMediumModal: false,
            uploadingFileProgress: 0,
            neighborhoodList: [],
            customer: {
                name: '',
                email: '',
                neighborhood_id: null,
                phone: '',
                street: '',
                letter: '',
                letter2: '',
                letter3: '',
                letter4: '',
                number: '',
                address: '',
                map_url: '',
                crossover: null,
                crossover2: null,
                referency: ""
            },
            errors: {}
        }
    },
    mounted() {
        this.getFilters();
        this.getCustomer();
    },
    watch: {
        customer: {
            handler() {
                this.validateAllFieldsCustomer()
            },
            deep: true
        }
    },
    methods: {
        getFilters() {
            const self = this;
            self.loading.form = true;
            axios.get('api/dashboard/tickets/filters').then(function (response) {
                self.neighborhoodList = response.data.neighborhoodList;
                self.loading.form = false;
            }).catch(function (e) {
                console.log({e})
                self.loading.form = false;
            })
        },
        getCustomer() {
            const self = this;

            axios.get('api/dashboard/admin/customers/' + self.$route.params.id, self.customer).then(function (response) {
                const customer = response.data;
                self.customer.name = customer.name;
                self.customer.email = customer.email;
                self.customer.phone = customer.phone?.replace(/^\+52/, '') || '';
                self.customer.address = customer.address;
                self.customer.map_url = customer.map_url || '';
                self.customer.number = customer.number;
                self.customer.street = customer.street;
                self.customer.letter = customer.letter;
                self.customer.letter2 = customer.letter2;
                self.customer.letter3 = customer.letter3;
                self.customer.letter4 = customer.letter4;
                self.customer.crossover = customer.crossovers;
                self.customer.crossover2 = customer.crossovers2 || '';
                self.customer.neighborhood_id = customer.neighborhood?.id || null;
                self.customer.referency = customer.referency;

                self.loading.form = false;
            }).catch(function () {
                self.loading.form = false;
            })
        },
        async saveCustomer() {
            const self = this;

            await self.validateAllFieldsCustomer();
            
            if (Object.values(self.errors).some(error => error)) return;

            self.loading.form = true;
            
            const customer = { ...self.customer };
            customer.phone = !customer.phone.startsWith('+52') ? `+52${customer.phone}` : customer.phone;

            axios.put('api/dashboard/admin/customers/' + self.$route.params.id, customer).then(function (response) {
                self.$notify({
                    title: self.$i18n.t('Success').toString(),
                    text: self.$i18n.t('Data saved correctly').toString(),
                    type: 'success'
                });
                self.loading.form = false;
            }).catch(function () {
                self.loading.form = false;
            });
        },
        async validateAllFieldsCustomer() {
            const customerFormSchema = yup.object().shape({
                name: yup.string().required('El nombre es obligatorio.'),
                // email: yup.string().required('El correo es obligatorio.').email('Debe ser un correo electrónico válido.'),
                phone: yup.string()
                    .matches(/^(\+52)?[1-9][0-9]{9}$/, 'Número de teléfono no válido. Debe ser un número de 10 dígitos que comience con un número válido.')
                    .required('El teléfono es obligatorio.'),
                neighborhood_id: yup.string().required('La colonia es obligatorio'),
                street: yup.string().required('La calle es obligatorio.'),
                // letter: yup.string().required('La letra es obligatorio.'),
                number: yup.string().required('El número es obligatorio.'),
                address: yup.string().required('La dirección es obligatorio.'),
                map_url: yup.string().url('Por favor ingresa una URL válida.'),
                crossover: yup.string().nullable().required('El cruzamiento es obligatorio.'),
            });

            try {
                await customerFormSchema.validate(this.customer, { abortEarly: false });
                Object.keys(this.errors).forEach(field => {
                    this.$set(this.errors, field, '');
                });
            } catch (validationErrors) {
                Object.keys(this.errors).forEach(field => {
                    this.$set(this.errors, field, '');
                });
                validationErrors.inner.forEach(error => {
                    this.$set(this.errors, error.path, error.message);
                });
            }
        },
        deleteCustomer() {
            const self = this;
            axios.delete('api/dashboard/admin/customers/' + self.$route.params.id).then(function () {
                self.$notify({
                    title: self.$i18n.t('Success').toString(),
                    text: self.$i18n.t('Data deleted successfully').toString(),
                    type: 'success'
                });
                self.$router.push('/dashboard/admin/customers');
            }).catch(function () {
                self.deleteLabelModal = false;
            });
        }
    },
}
</script>
